import { Component } from '@theme/component';

/**
 * @typedef {Object} AxTestimonialsRefs
 * @property {HTMLElement[]} [carousels] - Carousel containers (1 desktop+mobile, or 1 mobile-only)
 */

/**
 * @typedef {Object} SwiperInfo
 * @property {any} swiper
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement | null} prevBtn
 * @property {HTMLButtonElement | null} nextBtn
 * @property {HTMLElement} carousel
 */

/**
 * Testimonials — Swiper-based carousel with SSR fallback swap.
 *
 * Mirrors the init pattern used by AxTabbedBase (programmatic Swiper init,
 * SSR fallback → swiper-slide swap, arrow disabled-state sync, lazy preload)
 * minus the tab-switching logic.
 *
 * @extends {Component<AxTestimonialsRefs>}
 */
class AxTestimonialsComponent extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  /** @type {Map<HTMLElement, SwiperInfo>} */
  #swipers = new Map();

  async connectedCallback() {
    super.connectedCallback();

    await customElements.whenDefined('swiper-container');

    for (const carousel of this.refs.carousels ?? []) {
      this.#initSwiperForCarousel(carousel);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const info of this.#swipers.values()) {
      try {
        info.swiper?.destroy(true, true);
      } catch (_) {
        // noop — Swiper can throw during teardown if already destroyed
      }
    }
    this.#swipers.clear();
  }

  previous() {
    this.#visibleSwiper()?.slidePrev();
  }

  next() {
    this.#visibleSwiper()?.slideNext();
  }

  /**
   * @param {HTMLElement} carousel
   */
  #initSwiperForCarousel(carousel) {
    if (this.#swipers.has(carousel)) return;

    const swiperEl = /** @type {HTMLElement & { swiper: any; initialize: () => void }} */ (
      carousel.querySelector('swiper-container.ax-testimonials__swiper')
    );
    const fallback = carousel.querySelector('[data-ax-fallback]');
    if (!swiperEl || !fallback) return;

    const slideElements = [...fallback.children];
    if (!slideElements.length) return;

    // Reserve current height so the swap doesn't collapse layout (FOUC)
    const reservedHeight = carousel.getBoundingClientRect().height;
    if (reservedHeight > 0) {
      carousel.style.minHeight = `${reservedHeight}px`;
    }

    for (const slideEl of slideElements) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slideEl);
      swiperEl.appendChild(swiperSlide);
    }

    const slides = [...swiperEl.querySelectorAll('swiper-slide')];
    fallback.remove();

    Object.assign(swiperEl, this.#getSwiperConfig(swiperEl));

    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    requestAnimationFrame(() => {
      carousel.style.minHeight = '';
    });

    const swiper = swiperEl.swiper;
    if (!swiper) return;

    const prevBtn = /** @type {HTMLButtonElement | null} */ (
      carousel.querySelector('.ax-testimonials__arrows [ref="previous"]')
    );
    const nextBtn = /** @type {HTMLButtonElement | null} */ (
      carousel.querySelector('.ax-testimonials__arrows [ref="next"]')
    );

    /** @type {SwiperInfo} */
    const info = { swiper, slides, prevBtn, nextBtn, carousel };
    this.#swipers.set(carousel, info);

    const syncDisabled = () => {
      if (prevBtn) prevBtn.disabled = Boolean(swiper.isBeginning);
      if (nextBtn) nextBtn.disabled = Boolean(swiper.isEnd);
    };
    syncDisabled();
    swiper.on('slideChange', syncDisabled);
    swiper.on('reachBeginning', syncDisabled);
    swiper.on('reachEnd', syncDisabled);
    swiper.on('fromEdge', syncDisabled);
    swiper.on('resize', syncDisabled);

    this.#preloadAdjacent(slides, swiper.realIndex ?? 0, { currentOnly: true });

    const preloadNeighbors = () => {
      this.#preloadAdjacent(slides, swiper.realIndex ?? 0);
    };
    swiper.on('touchStart', preloadNeighbors);
    swiper.on('slideChangeTransitionStart', preloadNeighbors);

    swiper.update();
  }

  /**
   * @param {HTMLElement & { dataset: DOMStringMap }} swiperEl
   * @returns {Record<string, any>}
   */
  #getSwiperConfig(swiperEl) {
    const gapDesktop = parseInt(swiperEl.dataset.gapDesktop ?? '0', 10) || 0;
    const gapMobile = parseInt(swiperEl.dataset.gapMobile ?? '0', 10) || 0;

    return {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      spaceBetween: gapMobile,
      breakpoints: {
        750: { spaceBetween: gapDesktop },
      },
    };
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {number} index
   * @param {{ currentOnly?: boolean }} [options]
   */
  #preloadAdjacent(slides, index, { currentOnly = false } = {}) {
    if (!slides.length) return;

    const offsets = currentOnly ? [0] : [0, 1, -1];

    for (const offset of offsets) {
      const target = slides[index + offset];
      if (target?.querySelector('[data-lazy-keep]')) {
        document.dispatchEvent(
          new CustomEvent('removeLazyKeep', {
            detail: { lazyParentElement: target },
          })
        );
      }
    }
  }

  /**
   * Pick the swiper that is currently visible. With grid-desktop + carousel-mobile,
   * there are two carousels in the DOM, but only one is rendered (the other has
   * `hidden--desktop` or `hidden--mobile` applied). `offsetParent === null` flags
   * the hidden one.
   *
   * @returns {any | undefined}
   */
  #visibleSwiper() {
    for (const [carousel, info] of this.#swipers) {
      if (carousel.offsetParent !== null) return info.swiper;
    }
    return this.#swipers.values().next().value?.swiper;
  }
}

customElements.define('ax-testimonials-component', AxTestimonialsComponent);
